import { motion } from "framer-motion";
import { TrendingUp, Target, Lightbulb, BarChart2, Globe, CheckCircle2 } from "lucide-react";
import { methods } from "@/data";

const iconMap: Record<string, React.ElementType> = {
  Repeat: TrendingUp, Search: Target, Sparkles: Lightbulb, BarChart2, Globe,
};

const colors = [
  "from-blue-700 to-blue-900",
  "from-indigo-700 to-indigo-900",
  "from-violet-700 to-violet-900",
  "from-sky-700 to-sky-900",
  "from-teal-700 to-teal-900",
];

export default function Methods() {
  return (
    <div className="py-12 px-4">
      <div className="container mx-auto max-w-5xl">
        <div className="text-center mb-10">
          <p className="text-sm font-semibold text-primary uppercase tracking-widest mb-2">In the Classroom</p>
          <h1 className="text-4xl font-bold text-foreground mb-3">Innovative Teaching-Learning Methods</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            GBSRC employs five research-backed teaching methods that shift students from passive recipients to active architects of their own learning.
          </p>
        </div>

        {/* Method cards */}
        <div className="space-y-6 mb-14">
          {methods.map((m, i) => {
            const Icon = iconMap[m.icon] ?? Target;
            return (
              <motion.div
                key={m.title}
                initial={{ opacity: 0, x: i % 2 === 0 ? -30 : 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex gap-6 bg-background border border-border rounded-2xl p-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-all"
              >
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colors[i]} flex items-center justify-center shrink-0`}>
                  <Icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-primary border border-primary/30 px-2 py-0.5 rounded">
                      Method {i + 1}
                    </span>
                    <h2 className="text-lg font-bold text-foreground">{m.title}</h2>
                  </div>
                  <p className="text-foreground/70 leading-relaxed">{m.description}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Comparison overview */}
        <div className="bg-primary/5 border border-primary/20 rounded-2xl p-8">
          <h2 className="text-xl font-bold text-foreground mb-6">How These Methods Transform Learning</h2>
          <div className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {[
              ["Traditional: Passive listening", "GBSRC: Active engagement & application"],
              ["Traditional: Memorisation tests", "GBSRC: Competency demonstration"],
              ["Traditional: Single discipline", "GBSRC: Multidisciplinary integration"],
              ["Traditional: Theoretical only", "GBSRC: Real-world problem-solving"],
              ["Traditional: Teacher-centric", "GBSRC: Learner-centric facilitation"],
              ["Traditional: Annual exams only", "GBSRC: Continuous comprehensive evaluation"],
            ].map(([old, newway]) => (
              <div key={old} className="flex flex-col gap-1 border-b border-border pb-3">
                <p className="text-xs text-muted-foreground line-through">{old}</p>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                  <p className="text-sm font-medium text-foreground">{newway}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
